import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { lerMeuBonus, marcarLidos, reais, valorOuVazio } from '../../metas';
import { useValoresOcultos } from '../../design/sigilo';
import type { MesFechado, MetaNoMes, MeuBonus, MovimentoDoBonus } from '../../metas';
import './meu-bonus.css';

/**
 * O TOPO DO MEU DESEMPENHO (dono, 29/09 — fase 3): "quanto estou ganhando, quais metas estou
 * batendo, meu nível em cada uma, quanto falta, onde estou piorando, quanto posso ganhar a mais,
 * e se o saldo está bloqueado". Tudo é bônus PROJETADO até o fechamento.
 *
 * A tela não calcula dinheiro: nível, prêmio, falta, risco e potencial vêm do servidor. Aqui só se
 * escolhe o que mostrar e em que palavras. Sem meta ativa, este topo não aparece.
 */

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const competenciaLegivel = (c: string) => {
  const m = MESES[Number(c.slice(5, 7)) - 1] ?? '';
  return `${m.charAt(0).toUpperCase()}${m.slice(1)}/${c.slice(0, 4)}`;
};
const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const nivelNome = (m: MetaNoMes, k: number) =>
  m.nomesDosNiveis[k - 1]?.trim() || `Nível ${String(k)}`;

/** Distância em palavras: R$ no dinheiro, "p.p." em porcentagem. */
function distancia(m: MetaNoMes, v: string | null) {
  if (v === null) return '';
  if (m.unidade === 'PCT')
    return `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: m.casas, maximumFractionDigits: m.casas })} p.p.`;
  return valorOuVazio(v, m.unidade, m.casas);
}

export function MeuBonusEMetas({ sessao }: { sessao: Sessao }) {
  const ocultos = useValoresOcultos();
  const [b, setB] = useState<MeuBonus | null>(null);
  const [falhou, setFalhou] = useState(false);
  const carregar = useCallback(() => {
    setFalhou(false);
    void lerMeuBonus().then((r) => (r.ok ? setB(r.dados) : setFalhou(true)));
  }, []);
  useEffect(carregar, [carregar]);

  if (falhou)
    return (
      <section className="mb-falha" role="alert">
        <b>Não foi possível carregar o seu bônus agora.</b>
        <button type="button" className="mb-link" onClick={carregar}>
          Tentar de novo
        </button>
      </section>
    );
  if (!b)
    return (
      <section className="mb-esqueleto" aria-busy="true" aria-label="Carregando o seu bônus">
        <i />
        <i />
      </section>
    );
  const metas = b.mes?.metas ?? [];
  if (!metas.length && !b.anteriores.length) return null;

  const pagas = metas.filter((m) => m.comPremio);
  const outras = metas.filter((m) => !m.comPremio);
  const naoLidos = b.movimentos.filter((m) => !m.lido);
  return (
    <div className="mb">
      {naoLidos.length > 0 && (
        <Alertas
          movimentos={naoLidos}
          metas={metas}
          ocultos={ocultos}
          aoEntender={() => {
            void marcarLidos(
              sessao,
              naoLidos.map((m) => m.id),
            ).then(carregar);
          }}
        />
      )}
      {pagas.length > 0 && b.mes && <CartaoDoBonus b={b} pagas={pagas} />}
      {pagas.length > 0 && (
        <section aria-label="Minhas metas com premiação">
          <h2 className="mb-secao">Minhas metas com premiação</h2>
          <div className="mb-metas">
            {pagas.map((m) => (
              <CartaoDaMeta key={m.metaId} m={m} />
            ))}
          </div>
        </section>
      )}
      {outras.length > 0 && (
        <section aria-label="Outras metas">
          <h2 className="mb-secao">Outras metas</h2>
          <div className="mb-outras">
            {outras.map((m) => (
              <MetaDeAcompanhamento key={m.metaId} m={m} />
            ))}
          </div>
        </section>
      )}
      {b.movimentos.length > 0 && (
        <Movimentacoes movimentos={b.movimentos} metas={metas} ocultos={ocultos} />
      )}
      {b.anteriores.length > 0 && <MesesAnteriores meses={b.anteriores} ocultos={ocultos} />}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Meu bônus
 * ------------------------------------------------------------------ */

function CartaoDoBonus({ b, pagas }: { b: MeuBonus; pagas: MetaNoMes[] }) {
  const ocultos = useValoresOcultos();
  const x = b.mes?.bonus;
  if (!x) return null;
  const pct =
    Number(x.potencialMaximo) > 0
      ? Math.min(100, Math.round((Number(x.projetado) / Number(x.potencialMaximo)) * 100))
      : 0;
  // sem R$ (premio vem null), "com bônus" é quem já chegou a algum nível
  const comBonus = pagas.filter((m) =>
    ocultos ? (m.avaliacao?.nivel ?? 0) > 0 : Number(m.avaliacao?.premio ?? '0') > 0,
  ).length;
  const noMaximo = pagas.filter(
    (m) => m.avaliacao?.situacao === 'VALIDA' && m.avaliacao.nivel === m.avaliacao.nivelMaximo,
  ).length;
  const emRisco = pagas.filter((m) => m.avaliacao?.risco).length;
  const insight = melhorInsight(pagas, ocultos);
  const estado = {
    PROJETADO: {
      icone: 'lock' as const,
      rotulo: 'Bônus projetado',
      texto: 'Este valor pode aumentar ou diminuir até o fechamento do mês.',
    },
    CONFIRMADO: {
      icone: 'check' as const,
      rotulo: 'Bônus confirmado',
      texto: 'Competência fechada: este é o valor do mês.',
    },
    PAGO: { icone: 'money' as const, rotulo: 'Bônus pago', texto: 'Valor pago.' },
  }[b.estado];
  return (
    <section className="mb-bonus" aria-label="Meu bônus do mês">
      <div className="mb-bonus-topo">
        <div>
          <span className="mb-rotulo">
            {ocultos ? 'Minhas metas' : 'Meu bônus do mês'} · {competenciaLegivel(b.competencia)}
          </span>
          {!ocultos && <strong className="mb-bonus-valor">{reais(x.projetado)}</strong>}
          {!ocultos && (
            <span className={`mb-estado mb-estado-${b.estado.toLowerCase()}`}>
              <Icone nome={estado.icone} tamanho={12} traco={2} /> {estado.rotulo}
            </span>
          )}
          {!ocultos && <p className="mb-nota">{estado.texto}</p>}
        </div>
        {!ocultos && (
          <dl className="mb-bonus-numeros">
            <div>
              <dt>Potencial máximo</dt>
              <dd>{reais(x.potencialMaximo)}</dd>
            </div>
            <div>
              <dt>Já conquistado (projetado)</dt>
              <dd>{reais(x.projetado)}</dd>
            </div>
            <div>
              <dt>Falta para o máximo</dt>
              <dd>{reais(x.faltaParaMaximo)}</dd>
            </div>
          </dl>
        )}
      </div>
      {!ocultos && (
        <>
          <div
            className="mb-barra"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Bônus projetado sobre o potencial"
          >
            <i style={{ width: `${String(pct)}%` }} />
          </div>
          <div className="mb-barra-legenda">
            <span>
              {reais(x.projetado)} de {reais(x.potencialMaximo)}
            </span>
            <b>{pct}%</b>
          </div>
        </>
      )}
      {!ocultos && x.teto !== null && Number(x.excedente) > 0 && (
        <p className="mb-teto">
          Pelas metas: <b>{reais(x.pelasMetas)}</b> · limite mensal <b>{reais(x.teto)}</b> · bônus
          projetado <b>{reais(x.projetado)}</b> ({reais(x.excedente)} acima do limite)
        </p>
      )}
      <ul className="mb-resumo">
        <li>
          <b>{pagas.length}</b> metas com premiação
        </li>
        <li>
          <b>{comBonus}</b> com bônus
        </li>
        <li>
          <b>{noMaximo}</b> no máximo
        </li>
        <li className={emRisco ? 'mb-resumo-risco' : ''}>
          <b>{emRisco}</b> em risco
        </li>
        {!ocultos && Number(x.proximoGanho) > 0 && (
          <li className="mb-resumo-ganho">
            próximo ganho possível <b>+{reais(x.proximoGanho)}</b>
          </li>
        )}
      </ul>
      {insight && (
        <p className="mb-insight">
          <Icone nome="up-t" tamanho={14} traco={1.8} /> {insight}
        </p>
      )}
      <p className="mb-datas">
        Fechamento {ddmm(b.fechamento)} · pagamento previsto {ddmm(b.pagamento)}
      </p>
    </section>
  );
}

/** A dica do próximo ganho: a meta paga mais perto de subir (em proporção do alvo). */
function melhorInsight(pagas: MetaNoMes[], ocultos: boolean): string | null {
  const candidatas = pagas
    .filter(
      (m) =>
        m.avaliacao?.situacao === 'VALIDA' &&
        !m.avaliacao.bloqueio &&
        m.avaliacao.proximo &&
        (ocultos
          ? m.avaliacao.proximo.falta !== null && m.avaliacao.proximo.alvo !== null
          : Number(m.avaliacao.proximo.ganho) > 0),
    )
    .map((m) => {
      const p = m.avaliacao?.proximo;
      return {
        m,
        p,
        rel: p ? Math.abs(Number(p.falta)) / Math.max(1e-9, Math.abs(Number(p.alvo))) : Infinity,
      };
    })
    .sort((a, b) => a.rel - b.rel);
  const c = candidatas[0];
  if (!c?.p) return null;
  const d = distancia(c.m, c.p.falta);
  if (ocultos)
    return c.m.direcao === 'MENOR_MELHOR'
      ? `Reduza ${c.m.rotulo.toLowerCase()} em ${d} para subir de nível.`
      : `Você está a ${d} de subir um nível em ${c.m.nome}.`;
  return c.m.direcao === 'MENOR_MELHOR'
    ? `Reduza ${c.m.rotulo.toLowerCase()} em ${d} para liberar mais ${reais(c.p.ganho)}.`
    : `Você está a ${d} de subir um nível em ${c.m.nome} (+${reais(c.p.ganho)}).`;
}

/* ------------------------------------------------------------------ *
 * A meta com a régua
 * ------------------------------------------------------------------ */

function CartaoDaMeta({ m }: { m: MetaNoMes }) {
  const ocultos = useValoresOcultos();
  const a = m.avaliacao;
  const alvos = a?.alvos ?? [];
  // meta em R$ no modo "só números": o servidor manda base/valor/alvos nulos — só o nível aparece
  const semNumeros = m.unidade === 'RS' && (ocultos || m.valor === null);
  const regra = `${semNumeros ? '' : `Base: ${m.base === null ? 'sem base' : valorOuVazio(m.base, m.unidade, m.casas)} · `}${String(a?.nivelMaximo ?? alvos.length)} níveis · ${m.direcao === 'MENOR_MELHOR' ? 'menor é melhor' : 'maior é melhor'}${ocultos ? '' : ` · paga até ${reais(m.premioMaximo)}`}`;
  return (
    <article className={`mb-meta${a?.risco ? ' mb-meta-risco' : ''}`} title={regra}>
      <header className="mb-meta-cabeca">
        <div>
          <b className="mb-meta-nome">{m.nome}</b>
          <span className="mb-meta-ind">{m.rotulo}</span>
        </div>
        {!ocultos && (
          <div className="mb-meta-premio">
            <span>
              {a && a.situacao === 'VALIDA' && !a.bloqueio ? 'bônus' : 'bônus (não conta ainda)'}
            </span>
            <b>
              +
              {reais(
                a?.situacao === 'VALIDA' && !a.bloqueio ? a.premio : (a?.premioSeValidar ?? '0'),
              )}
            </b>
          </div>
        )}
      </header>

      {m.situacao === 'NAO_COMECOU' ? (
        <p className="mb-meta-msg">
          Esta meta começa em {m.periodo ? ddmm(m.periodo.de) : 'breve'}.
        </p>
      ) : m.situacao === 'SEM_DADO' ? (
        <p className="mb-meta-msg">Sem vendas suficientes para calcular este indicador ainda.</p>
      ) : m.situacao === 'SEM_BASE' ? (
        <p className="mb-meta-msg">Ainda não há histórico seu para montar a base desta meta.</p>
      ) : (
        a && (
          <>
            <div className="mb-meta-atual">
              {!semNumeros && (
                <div>
                  <span>Atual</span>
                  <strong>{valorOuVazio(m.valor, m.unidade, m.casas)}</strong>
                </div>
              )}
              {!semNumeros && (
                <div>
                  <span>Base</span>
                  <b>{m.base === null ? '—' : valorOuVazio(m.base, m.unidade, m.casas)}</b>
                </div>
              )}
              <div>
                <span>Nível</span>
                <b>
                  {a.nivel} de {a.nivelMaximo}
                </b>
              </div>
            </div>
            {semNumeros ? (
              <div
                className="mb-barra"
                role="progressbar"
                aria-valuenow={a.nivel}
                aria-valuemin={0}
                aria-valuemax={a.nivelMaximo}
                aria-label={`Nível de ${m.nome}`}
              >
                <i
                  style={{
                    width: `${String(a.nivelMaximo > 0 ? Math.round((a.nivel / a.nivelMaximo) * 100) : 0)}%`,
                  }}
                />
              </div>
            ) : (
              <Regua m={m} ocultos={ocultos} />
            )}
            <ul className="mb-meta-frases">
              <li>
                {a.nivel > 0
                  ? `Você está no ${nivelNome(m, a.nivel)}.`
                  : 'Você ainda não chegou ao primeiro nível.'}
              </li>
              {a.proximo ? (
                <li>
                  {a.proximo.falta === null ? (
                    <>Falta subir para o {nivelNome(m, a.proximo.nivel)}.</>
                  ) : (
                    <>
                      Faltam <b>{distancia(m, a.proximo.falta)}</b> para o{' '}
                      {nivelNome(m, a.proximo.nivel)}.
                    </>
                  )}{' '}
                  {!ocultos && Number(a.proximo.ganho) > 0 && (
                    <>
                      Próximo ganho: <b>+{reais(a.proximo.ganho)}</b>.
                    </>
                  )}
                </li>
              ) : (
                <li>Você está no nível máximo desta meta.</li>
              )}
              {a.risco && (
                <li className="mb-frase-risco">
                  <Icone nome="warn" tamanho={13} traco={1.8} /> Nível em risco:{' '}
                  {a.risco.limite === null ? (
                    'pode voltar'
                  ) : (
                    <>
                      se {m.direcao === 'MENOR_MELHOR' ? 'passar de' : 'cair abaixo de'}{' '}
                      {valorOuVazio(a.risco.limite, m.unidade, m.casas)}, volta
                    </>
                  )}{' '}
                  ao {a.nivel > 1 ? nivelNome(m, a.nivel - 1) : 'início'}.
                  {!ocultos && (
                    <>
                      {' '}
                      Bônus projetado em risco: <b>{reais(a.risco.valorEmRisco)}</b>.
                    </>
                  )}
                </li>
              )}
              {a.situacao === 'PARCIAL' && (
                <li className="mb-frase-parcial">
                  Resultado parcial: você ainda não atingiu a amostra mínima para validar este
                  indicador.
                </li>
              )}
              {a.bloqueio && (
                <li className="mb-frase-bloqueio">
                  Meta atingida, mas o bônus está bloqueado por requisito: {a.bloqueio.join(' · ')}
                </li>
              )}
            </ul>
          </>
        )
      )}
    </article>
  );
}

/**
 * A RÉGUA: base, cada nível com o alvo e o prêmio, e o ponteiro "▲ atual". Posição linear entre o
 * menor e o maior número da régua (só desenho — os números são os do servidor).
 */
function Regua({ m, ocultos }: { m: MetaNoMes; ocultos: boolean }) {
  const a = m.avaliacao;
  const alvos = a?.alvos ?? [];
  const pontos = [m.base, ...alvos].filter((x): x is string => x !== null).map(Number);
  const valor = m.valor === null ? null : Number(m.valor);
  const todos = valor === null ? pontos : [...pontos, valor];
  const min = Math.min(...todos);
  const max = Math.max(...todos);
  const menorMelhor = m.direcao === 'MENOR_MELHOR';
  // "Melhor" sempre à direita: no desconto a régua corre ao contrário.
  const pos = (v: number) => {
    const t = max === min ? 0.5 : (v - min) / (max - min);
    return `${String(Math.round((menorMelhor ? 1 - t : t) * 1000) / 10)}%`;
  };
  return (
    <div className="mb-regua-rolo">
      <div className="mb-regua" aria-label={`Régua de ${m.nome}`}>
        <div className="mb-regua-trilho">
          {valor !== null && <i className="mb-regua-feito" style={{ width: pos(valor) }} />}
        </div>
        {m.base !== null && (
          <span className="mb-regua-marco mb-regua-base" style={{ left: pos(Number(m.base)) }}>
            <em>Base</em>
            <b>{valorOuVazio(m.base, m.unidade, m.casas)}</b>
          </span>
        )}
        {alvos.map((x, i) => (
          <span
            key={x}
            className={`mb-regua-marco${a && a.nivel > i ? ' mb-regua-batido' : ''}`}
            style={{ left: pos(Number(x)) }}
          >
            <em>N{i + 1}</em>
            <b>{valorOuVazio(x, m.unidade, m.casas)}</b>
            {m.comPremio && !ocultos && <small>{reais(m.premiosDosNiveis?.[i])}</small>}
          </span>
        ))}
        {valor !== null && (
          <span
            className="mb-regua-ponteiro"
            style={{ left: pos(valor) }}
            aria-label={`Você: ${valorOuVazio(m.valor, m.unidade, m.casas)}`}
          >
            ▲ {valorOuVazio(m.valor, m.unidade, m.casas)}
          </span>
        )}
      </div>
    </div>
  );
}

function MetaDeAcompanhamento({ m }: { m: MetaNoMes }) {
  const a = m.avaliacao;
  const proximoAlvo = a?.proximo?.alvo ?? null;
  return (
    <div className="mb-outra">
      <div>
        <b>{m.nome}</b>
        <span>Indicador de acompanhamento · sem prêmio</span>
      </div>
      <div className="mb-outra-valor">
        {m.valor !== null && <strong>{valorOuVazio(m.valor, m.unidade, m.casas)}</strong>}
        {a?.proximo ? (
          <span>
            próximo:{' '}
            {proximoAlvo === null
              ? nivelNome(m, a.proximo.nivel)
              : `${valorOuVazio(proximoAlvo, m.unidade, m.casas)} (${nivelNome(m, a.proximo.nivel)})`}
          </span>
        ) : a && a.nivel > 0 ? (
          <span>no nível máximo</span>
        ) : (
          <span>{m.situacao === 'SEM_DADO' ? 'sem dado ainda' : ''}</span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Alertas e movimentações
 * ------------------------------------------------------------------ */

function textoDoMovimento(v: MovimentoDoBonus, m: MetaNoMes | undefined, ocultos: boolean) {
  const valor = m && v.valor !== null ? valorOuVazio(v.valor, m.unidade, m.casas) : null;
  // "+R$ x no seu bônus projetado" só com valores à vista
  const dif = ocultos || v.diferenca === null ? null : v.diferenca.replace(/^-/, '');
  const nome = m ? m.rotulo : v.meta;
  switch (v.tipo) {
    case 'SUBIU':
      return {
        titulo: `Parabéns! ${nome} subiu${valor ? ` para ${valor}` : ''}.`,
        texto: `Você alcançou o Nível ${String(v.nivelDepois)}.${dif === null ? '' : ` +${reais(dif)} no seu bônus projetado.`}`,
      };
    case 'MAXIMO':
      return {
        titulo: `Nível máximo em ${v.meta}!`,
        texto: `${nome}${valor ? ` em ${valor}` : ''}.${dif === null ? '' : ` +${reais(dif)} no seu bônus projetado.`}`,
      };
    case 'CAIU':
      return {
        titulo: `Atenção: ${nome} ${m?.direcao === 'MENOR_MELHOR' ? 'subiu' : 'caiu'}${valor ? ` para ${valor}` : ''}.`,
        texto: `Você voltou para o Nível ${String(v.nivelDepois)}.${dif === null ? '' : ` Seu bônus projetado reduziu em ${reais(dif)}.`}`,
      };
    case 'RISCO':
      return {
        titulo: `Nível em risco em ${v.meta}.`,
        texto: `${nome}${valor ? ` está em ${valor}` : ''}, perto do limite do Nível ${String(v.nivelDepois)}.`,
      };
    case 'PERTO':
      return {
        titulo: 'Falta pouco!',
        texto: `${nome}${valor ? ` está em ${valor}` : ''} — pertinho do próximo nível em ${v.meta}.`,
      };
  }
}

function Alertas({
  movimentos,
  metas,
  ocultos,
  aoEntender,
}: {
  movimentos: readonly MovimentoDoBonus[];
  metas: readonly MetaNoMes[];
  ocultos: boolean;
  aoEntender: () => void;
}) {
  const v = movimentos[0];
  if (!v) return null;
  const t = textoDoMovimento(
    v,
    metas.find((m) => m.metaId === v.metaId),
    ocultos,
  );
  const bom = v.tipo === 'SUBIU' || v.tipo === 'MAXIMO' || v.tipo === 'PERTO';
  return (
    <div className={`mb-alerta${bom ? ' mb-alerta-bom' : ' mb-alerta-atencao'}`} role="status">
      <Icone nome={bom ? 'up-t' : 'warn'} tamanho={16} traco={1.8} />
      <div>
        <b>{t.titulo}</b>
        <span>{t.texto}</span>
        {movimentos.length > 1 && (
          <em>e mais {movimentos.length - 1} novidade(s) nas movimentações</em>
        )}
      </div>
      <button type="button" className="mb-link" onClick={aoEntender}>
        Entendi
      </button>
    </div>
  );
}

function Movimentacoes({
  movimentos,
  metas,
  ocultos,
}: {
  movimentos: readonly MovimentoDoBonus[];
  metas: readonly MetaNoMes[];
  ocultos: boolean;
}) {
  const hojeTxt = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const quando = (iso: string) => {
    const d = new Date(iso);
    const dia = d.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
    const hora = d.toLocaleTimeString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
    });
    const ontem = new Date(Date.parse(`${hojeTxt}T12:00:00Z`) - 86_400_000)
      .toISOString()
      .slice(0, 10);
    return `${dia === hojeTxt ? 'Hoje' : dia === ontem ? 'Ontem' : ddmm(dia)} · ${hora}`;
  };
  const dinheiro = movimentos.filter(
    (v) => v.tipo === 'SUBIU' || v.tipo === 'CAIU' || v.tipo === 'MAXIMO',
  );
  if (!dinheiro.length) return null;
  return (
    <section aria-label="Movimentações do bônus">
      <h2 className="mb-secao">Movimentações do bônus</h2>
      <ol className="mb-movs">
        {dinheiro.map((v) => {
          const m = metas.find((x) => x.metaId === v.metaId);
          const menos = v.diferenca?.startsWith('-') ?? false;
          return (
            <li key={v.id}>
              <span className="mb-mov-quando">{quando(v.em)}</span>
              <span className="mb-mov-meta">
                <b>{v.meta}</b>
                <em>
                  Nível {v.nivelAntes} → Nível {v.nivelDepois}
                  {m && v.valor !== null ? ` · ${valorOuVazio(v.valor, m.unidade, m.casas)}` : ''}
                </em>
              </span>
              {!ocultos && v.diferenca !== null && (
                <span className={`mb-mov-valor${menos ? ' mb-mov-menos' : ''}`}>
                  {menos ? '−' : '+'}
                  {reais(v.diferenca.replace(/^-/, ''))}
                  {menos ? ' projetado' : ''}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** Os meses FECHADOS: confirmado (a receber) ou pago — os valores não mudam mais. */
function MesesAnteriores({ meses, ocultos }: { meses: readonly MesFechado[]; ocultos: boolean }) {
  return (
    <section aria-label="Meses anteriores">
      <h2 className="mb-secao">Meses anteriores</h2>
      <ol className="mb-movs">
        {meses.map((m) => (
          <li key={m.competencia}>
            <span className="mb-mov-quando">{competenciaLegivel(m.competencia)}</span>
            <span className="mb-mov-meta">
              <b>
                <Icone nome={m.estado === 'PAGO' ? 'money' : 'check'} tamanho={12} traco={2} />{' '}
                {m.estado === 'PAGO'
                  ? `Bônus pago em ${ddmm(m.pagoEm ?? '')}`
                  : `Bônus confirmado · pagamento previsto ${ddmm(m.pagamentoPrevisto)}`}
              </b>
              <em>{m.metas.map((x) => `${x.nome} N${String(x.nivel)}`).join(' · ')}</em>
            </span>
            {!ocultos && <span className="mb-mov-valor">{reais(m.pagoValor ?? m.confirmado)}</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}
